import { createApi } from '@reduxjs/toolkit/query/react';

import { baseQueryWithAuth } from '../auth';
import { APIVersion } from '../../constants/keys';
import { WorkspaceApi } from '../../types';

interface WorkspaceResponse {
  count: number;
  next: string;
  previous: string;
  results: WorkspaceApi[];
}

interface WorkspaceArgs {
  ordering?: string;
  page?: number;
  per_page?: number;
  sensor__serial_number__nonexists?: string;
}

interface AddWorkspaceArgs {
  name: string;
  avatar?: string;
}

interface UpdateWorkspaceArgs extends AddWorkspaceArgs {
  workspaceId: string;
}

// Workspaces
export const workspacesApi = createApi({
  reducerPath: 'workspacesApi',
  baseQuery: baseQueryWithAuth,
  endpoints: (builder) => ({
    getWorkspaces: builder.query<WorkspaceApi[], WorkspaceArgs>({
      query: (params) => ({
        url: `/core-api/${APIVersion}/workspaces`,
        params,
      }),
      transformResponse: (data: WorkspaceResponse) => data.results,
    }),
    addWorkspace: builder.mutation<WorkspaceApi, AddWorkspaceArgs>({
      query: ({ name }) => ({
        url: `/core-api/${APIVersion}/workspaces`,
        method: 'POST',
        body: { name },
      }),
    }),
    updateWorkspace: builder.mutation<WorkspaceApi, UpdateWorkspaceArgs>({
      query: ({ workspaceId, ...fields }) => {
        const formData = new FormData();
        Object.entries(fields).forEach(([key, value]) => {
          if (value !== undefined && value !== null) {
            formData.append(key, value as string | Blob); // File → Blob
          }
        });
        return {
          url: `/core-api/${APIVersion}/workspaces/${workspaceId}`,
          method: 'PATCH',
          body: formData,
        };
      },
    }),
    deleteWorkspace: builder.mutation<void, { id: string }>({
      query: ({ id }) => ({
        url: `/core-api/${APIVersion}/workspaces/${id}`,
        method: 'DELETE',
      }),
    }),
  }),
});

export const {
  useGetWorkspacesQuery,
  useAddWorkspaceMutation,
  useUpdateWorkspaceMutation,
  useDeleteWorkspaceMutation,
} = workspacesApi;
